import {useState, useEffect, useMemo, useCallback, useRef} from 'react'
import {useNamespaceTranslations} from './useNamespacesTranslations'

const omitKey = (obj, key) => {
    if (!obj || !key) return obj || {}
    const rest = {...obj}
    delete rest[key]
    return rest
}

const normalizeYupError = (err, fieldName, fallbackMsg) => {
    if (!err) return {default: fallbackMsg}

    if (Array.isArray(err.inner) && err.inner.length) {
        const map = new Map()
        for (const e of err.inner) {
            if (!e?.path || map.has(e.path)) continue
            map.set(e.path, e.message)
        }
        return Object.fromEntries(map)
    }

    if (err.path && err.message) return {[err.path]: err.message}

    if (Array.isArray(err.errors) && err.errors.length) {
        if (fieldName) return {[fieldName]: err.errors[0]}
        return {default: err.errors[0]}
    }

    return {default: fallbackMsg}
}

const useDynamicValidation = (schemaCreator, options = {}) => {
    const [errors, setErrors] = useState({})
    const lastDataRef = useRef(null)
    const validationText = useNamespaceTranslations('validation')

    const {
        revalidateOnLanguageChange = true,
        enableLogging = false
    } = options

    const validationSchema = useMemo(() => {
        return schemaCreator(validationText)
    }, [validationText, schemaCreator, enableLogging])

    useEffect(() => {
        const hasPrevData = !!lastDataRef.current
        const hasErrors = Object.keys(errors).length > 0
        if (!revalidateOnLanguageChange || !hasPrevData || !hasErrors) return
        validateData(lastDataRef.current).catch(() => {})
    }, [validationText])

    const validateData = useCallback(
        async(data, fieldName = null) => {
            lastDataRef.current = data

            if (fieldName) {
                try {
                    await validationSchema.validateAt(fieldName, data)
                    setErrors(prev => omitKey(prev, fieldName))
                    return {isValid: true, errors: {}}
                } catch (err) {
                    const newErrors = normalizeYupError(
                        err,
                        fieldName,
                        validationText?.default_msg || 'Validation failed'
                    )
                    setErrors(prev => ({...prev, ...newErrors}))
                    return {isValid: false, errors: newErrors}
                }
            }

            try {
                await validationSchema.validate(data, {abortEarly: false})
                setErrors({})
                return {isValid: true}
            } catch (err) {
                const newErrors = normalizeYupError(
                    err,
                    null,
                    validationText?.default_msg || 'Validation failed'
                )
                setErrors({...newErrors})
                return {isValid: false, errors: newErrors}
            }
        },
        [validationSchema, validationText, enableLogging]
    )

    const clearErrors = useCallback((fieldName = null) => {
        if (!fieldName) {
            setErrors({})
            lastDataRef.current = null
            return
        }
        setErrors(prev => omitKey(prev, fieldName))
        lastDataRef.current = null
    }, [])

    return {
        errors,
        validationSchema,
        validateData,
        clearErrors,
        isValid: Object.keys(errors).length === 0
    }
}

export default useDynamicValidation
